import axios from "axios";
import {
  Button,
  FormRow,
  Input,
  Modal,
  SelectorSearch,
} from "globalComponents";
import { Position, User } from "globalResources/dataTypes";
import {
  errorHandler,
  formatRateInput,
  roundTwoDecimal,
} from "globalResources/util";
import React, { useEffect, useState } from "react";
import { Loader } from "semantic-ui-react";
import styled from "styled-components";
import { getTheme } from "Theme";
const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
`;

const initialPosition: Position = {
  _id: "",
  user: "",
  name: "employee",
  payRate: 0,
  billRate: 0,
  overtimePayRate: 0,
  overtimeBillRate: 0,
  isArchived: false,
  client: "",
};

const initialErrors = {
  ...initialPosition,
  position: "",
  payRate: "",
  billRate: "",
  overtimePayRate: "",
  overtimeBillRate: "",
};

const initialNumberInputs = {
  payRate: "0.00",
  billRate: "0.00",
  overtimePayRate: "0.00",
  overtimeBillRate: "0.00",
};

interface Props {
  id: string;
  clientId?: string;
  onClose: () => void;
}

const PositionModal = ({ id, clientId, onClose }: Props) => {
  const [position, setPosition] = useState(initialPosition);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState(initialErrors);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [numberInputs, setNumberInputs] = useState(initialNumberInputs);
  const [pagination, setPagination] = useState({
    limit: 50,
    page: 1,
    total: 0,
  });

  const fetchUsers = () => {
    axios
      .get("/users", {
        params: {
          ...pagination,
          search,
          searchBy: "name",
          sort: "firstName",
          direction: 1,
          accountType: ["employee", "admin", "super"],
        },
      })
      .then((res) => {
        let temp = res.data.users.map((u: User) => ({
          label:
            u.firstName + " " + u.lastName + ` (${u.accountType} ${u.UID})`,
          value: u._id,
        }));
        setUsers(temp);
        setPagination((old) => ({ ...old, total: res.data.total }));
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
      })
      .finally(() => setLoading(false));
  };

  const fetchPosition = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/position/${id}`);
      setPosition({
        ...res.data,
        user: res.data.user ? res.data.user._id : "",

        payRate: (parseFloat(res.data.payRate) / 100).toFixed(2),
        billRate: (parseFloat(res.data.billRate) / 100).toFixed(2),
        overtimePayRate: (parseFloat(res.data.overtimePayRate) / 100).toFixed(
          2
        ),
        overtimeBillRate: (parseFloat(res.data.overtimeBillRate) / 100).toFixed(
          2
        ),
      });

      setNumberInputs({
        payRate: (parseFloat(res.data.payRate) / 100).toFixed(2),
        billRate: (parseFloat(res.data.billRate) / 100).toFixed(2),
        overtimePayRate: (parseFloat(res.data.overtimePayRate) / 100).toFixed(
          2
        ),
        overtimeBillRate: (parseFloat(res.data.overtimeBillRate) / 100).toFixed(
          2
        ),
      });

      setSearch(res.data.user.firstName + " " + res.data.user.lastName);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios[position._id ? "patch" : "post"](
        `/position${position._id ? "/" + position._id : ""}`,
        {
          ...position,
          clientId,
          userId: position.user,
          payRate: roundTwoDecimal(numberInputs.payRate) * 100,
          billRate: roundTwoDecimal(numberInputs.billRate) * 100,
          overtimeBillRate:
            roundTwoDecimal(numberInputs.overtimeBillRate) * 100,
          overtimePayRate: roundTwoDecimal(numberInputs.overtimePayRate) * 100,
        }
      );
      onClose();
    } catch (err) {
      const errorMessage = await errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosition();
  }, [id]);

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const updateForm = (value: any, name: string) => {
    setPosition((old) => ({ ...old, [name]: value }));
  };

  const checkSubmit = () => {
    if (!position.user)
      setErrors((old) => ({
        ...old,
        user: "Required",
      }));
    else {
      setErrors((old) => ({
        ...old,
        position: "",
        user: "",
      }));

      submit();
    }
  };

  const archive = async () => {
    try {
      axios.patch(`/position/${position._id}/archive`);
      onClose();
    } catch (err) {
      const message = await errorHandler(err);
    }
  };

  return (
    <Modal onClose={onClose}>
      {loading ? (
        <Loader active size="large" />
      ) : (
        <Main>
          <FormRow>
            <SelectorSearch
              placeholder="Select a user"
              onSearch={(v: string) => setSearch(v)}
              label="Employee"
              options={users}
              value={position.user}
              onSelect={(v: string) => updateForm(v, "user")}
              error={errors.user}
            />
            <Input
              label="Position"
              value={position.name}
              onChange={(e: any) => updateForm(e.target.value, "name")}
              error={errors.position}
            />
          </FormRow>
          <FormRow>
            <Input
              type="currency"
              label="Bill Rate"
              value={numberInputs.billRate}
              onBlur={() => {
                setNumberInputs((old) => ({
                  ...old,
                  billRate: formatRateInput(numberInputs.billRate),
                  overtimeBillRate: formatRateInput(numberInputs.billRate, 1.5),
                }));
              }}
              onChange={(e: any) =>
                setNumberInputs({
                  ...numberInputs,
                  billRate: e.currentTarget.value,
                })
              }
              error={errors.billRate}
            />
            <Input
              type="currency"
              label="Pay Rate"
              value={numberInputs.payRate}
              onBlur={() => {
                setNumberInputs((old) => ({
                  ...old,
                  payRate: formatRateInput(numberInputs.payRate),
                  overtimePayRate: formatRateInput(numberInputs.payRate, 1.5),
                }));
              }}
              onChange={(e: any) =>
                setNumberInputs({
                  ...numberInputs,
                  payRate: e.currentTarget.value,
                })
              }
              error={errors.payRate}
            />
          </FormRow>
          <FormRow>
            <Input
              type="currency"
              label="Overtime Bill Rate"
              value={numberInputs.overtimeBillRate}
              onBlur={() => {
                setNumberInputs((old) => ({
                  ...old,
                  overtimeBillRate: formatRateInput(
                    numberInputs.overtimeBillRate
                  ),
                }));
              }}
              onChange={(e: any) =>
                setNumberInputs({
                  ...numberInputs,
                  overtimeBillRate: e.currentTarget.value,
                })
              }
              error={errors.overtimeBillRate}
            />
            <Input
              type="currency"
              label="Overtime Pay Rate"
              value={numberInputs.overtimePayRate}
              onBlur={() => {
                setNumberInputs((old) => ({
                  ...old,
                  overtimePayRate: formatRateInput(
                    numberInputs.overtimePayRate
                  ),
                }));
              }}
              onChange={(e: any) =>
                setNumberInputs({
                  ...numberInputs,
                  overtimePayRate: e.currentTarget.value,
                })
              }
              error={errors.overtimePayRate}
            />
          </FormRow>
          <FormRow>
            {position.user && (
              <Button color={theme.NEGATIVE} onClick={() => archive()}>
                Remove Employee
              </Button>
            )}
            <Button onClick={checkSubmit}>Save Position</Button>
          </FormRow>
        </Main>
      )}
    </Modal>
  );
};
export default PositionModal;
